// src/lib/situationQueue.js
//
// THE one place that mutates /api/victor/situation-queue. P1.5 IA-8a.
//
// Three components owned a private copy of this before 19 Sep 2026 —
// VictorPage (read/complete/dismiss), CommandCenter (read/complete/snooze/
// dismiss) and TodayPage (dismiss) — and two of the three treated a failed
// write as a success:
//
//   - VictorPage fired and forgot: `apiFetch(...).catch(() => {})` followed by
//     an unconditional local filter. Fixed in IA-1d; this module is that fix,
//     lifted so it applies everywhere rather than in one file.
//   - TodayPage:69 AWAITS but never reads `res.ok`. apiFetch resolves on a
//     500, so the await proves only that the network answered — it then
//     navigates to The Floor as though the dismiss landed.
//   - CommandCenter reads `data.items` but swallows every error into a
//     console.error the user never sees.
//
// Two facts about the endpoint that callers kept getting wrong, and that this
// module encodes once:
//
//   1. A REFUSED WRITE CAN ARRIVE AS HTTP 200. The handler answers
//      `{ success: false, error }` with a 200 in several paths. Checking the
//      status code alone is not enough; the body has to be read too.
//
//   2. EVERY MUTATION RETURNS THE REFRESHED LIST as `items`. That is the
//      authoritative post-write state — it accounts for the auto-generation
//      that `complete` triggers when fewer than three items remain
//      (situation-queue.js:230-241), which a local `.filter()` cannot know
//      about. CommandCenter used it; Victor and Today filtered locally and so
//      silently dropped a newly generated item until the next full load.
//
// Deliberately NOT a React hook: the three callers keep their state in
// different shapes (Victor splits active/handled, CommandCenter holds one
// list, Today holds mixed item types). A hook would have to own that shape and
// would force all three to converge on it, which is a much bigger change than
// this defect warrants. This owns the REQUEST and the ERROR RULE; the caller
// still owns its own state.

import { apiFetch } from './api';

const ENDPOINT = '/api/victor/situation-queue';

/**
 * Run one queue mutation.
 *
 * Resolves with the refreshed active list on success. THROWS on any failure —
 * transport, HTTP status, or a success:false body — so a caller that does not
 * catch fails loudly rather than continuing as though the write landed.
 *
 * @param {'complete'|'dismiss'|'snooze'} action
 * @param {string} id       the queue item id
 * @param {object} [extra]  extra body fields, e.g. { snoozeDays: 1 }
 * @returns {Promise<{items: Array}>}
 */
export async function runQueueAction(action, id, extra = {}) {
  if (!id) throw new Error('runQueueAction: an item id is required');

  const res = await apiFetch(`${ENDPOINT}?action=${action}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ id, ...extra }),
  });

  if (!res.ok) throw new Error(`Queue ${action} failed (${res.status})`);

  // A 200 is not the same as a success — see note 1 above. An unparseable body
  // is also a failure: it means something other than this handler answered.
  let data;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Queue ${action} returned an unreadable response`);
  }
  if (data.success === false) throw new Error(data.error || `Queue ${action} failed`);

  // `items` is absent from no success path today, but an older deploy answering
  // mid-rollout would omit it. Returning undefined would make a caller blank
  // its list on a write that actually succeeded, so fall back to null and let
  // the caller keep what it has.
  return { items: Array.isArray(data.items) ? data.items : null };
}

/** Mark an item Actioned. May trigger server-side auto-generation of a replacement. */
export const completeQueueItem = (id) => runQueueAction('complete', id);

/** Archive an item — dismissed items are shown nowhere. */
export const dismissQueueItem = (id) => runQueueAction('dismiss', id);

/** Hide an item for `days` days. */
export const snoozeQueueItem = (id, days = 1) => runQueueAction('snooze', id, { snoozeDays: days });
